import {
  ExcursionStatusCancelled,
  type Excursion,
  type ExcursionTemplateLimitsItem,
} from "api/excursions/Excursion.typed";

export function excursionTotals(ex: Excursion): {
  leadCount: number;
  peopleCount: number;
  groupCount: number;
  totalPeople: number;
} {
  const groups = ex.groups ?? [];
  let leadCount = 0;
  let peopleCount = 0;
  for (const g of groups) {
    leadCount += g.excursion_users?.length ?? 0;
    peopleCount += g.excursion_customers?.length ?? 0;
  }
  return {
    leadCount,
    peopleCount,
    groupCount: groups.length,
    totalPeople: leadCount + peopleCount,
  };
}

export function resolveExcursionTemplateLimits(
  paramsTemplateId: number | undefined,
  templatesList: ExcursionTemplateLimitsItem[] | undefined,
): { maxPeople?: number; maxGroups?: number } {
  if (paramsTemplateId == null) return {};
  const resolved = templatesList?.find((t) => t.id === paramsTemplateId);
  return {
    maxPeople: resolved?.max_people_per_excursion,
    maxGroups: resolved?.max_groups_per_excursion,
  };
}

/** Start–end slot label (`—` when both empty). */
export function excursionTimeRangeLabel(ex: Pick<Excursion, "start_time" | "end_time">): string {
  return [ex.start_time, ex.end_time].filter(Boolean).join(" – ") || "—";
}

/** Totals + template caps for `ExcursionTemplateLimitsChips` (planning drawer, excursion cards). */
export function buildExcursionTemplateLimitChipModel(
  excursion: Excursion,
  templatesList: ExcursionTemplateLimitsItem[] | undefined,
): {
  totalPeople: number;
  groupCount: number;
  maxPeople?: number;
  maxGroups?: number;
} {
  const { totalPeople, groupCount } = excursionTotals(excursion);
  const { maxPeople, maxGroups } = resolveExcursionTemplateLimits(
    excursion.params_template_id,
    templatesList,
  );

  return { totalPeople, groupCount, maxPeople, maxGroups };
}

/** Aggregated fill for a calendar day: sums people and caps over non-cancelled excursions with a max. */
export type PlanningDayFillRate = {
  totalPeople: number;
  totalMax: number;
  percent: number;
};

export function computePlanningDayFillRate(
  excursions: Excursion[],
  templatesList: ExcursionTemplateLimitsItem[] | undefined,
): PlanningDayFillRate | undefined {
  let totalPeople = 0;
  let totalMax = 0;

  for (const ex of excursions) {
    if (ex.status === ExcursionStatusCancelled) {
      continue;
    }

    const { totalPeople: tp } = excursionTotals(ex);
    const { maxPeople } = resolveExcursionTemplateLimits(ex.params_template_id, templatesList);

    if (maxPeople != null && maxPeople > 0) {
      totalPeople += tp;
      totalMax += maxPeople;
    }
  }

  if (totalMax <= 0) {
    return undefined;
  }

  const percent = Math.round((totalPeople / totalMax) * 100);

  return { totalPeople, totalMax, percent };
}

export type ExcursionPeopleFillTone = "ok" | "warning" | "full";

export function excursionPeopleFillTone(
  totalPeople: number,
  maxPeople: number | undefined,
): ExcursionPeopleFillTone | undefined {
  if (maxPeople == null || maxPeople <= 0) {
    return undefined;
  }

  if (totalPeople >= maxPeople) {
    return "full";
  }

  if (totalPeople >= 0.8 * maxPeople) {
    return "warning";
  }

  return "ok";
}
